import React from 'react';
import { calculateTextWidthCavnas, DEFAULT_COMPOSED_CHART_X_HEIGHT } from '@components/Widgets/utils';
import { DateCondition, FeatureFlags, PeriodBehavior } from '@fabric-ui/components';
import {
  DeliveryMethodOptions,
  FileFormat,
  FormatterType,
  generateInnerDefaultFormatter,
  HeadCellType,
  TimeBucket,
} from '@fabric-ui/components/Table';
import { Down, Up } from '@fabric-ui/icons';
import { NoChange } from '@fabric-ui/icons/emptyStates';
import { flex, getCssVarColorWithOpacity } from '@fabric-ui/theme';
import { abbreviateNumber } from '@fabric-ui/utils';
import { Box, LinearProgress, SxProps } from '@mui/material';
import { downloadBlob, generateID, generateUUID, getPercent, isNullOrUndefined } from '@utils/utils';
import { APP_PATHS } from '../../../types';
import { FormattingRule } from '../../FormattingRules/types';
import { getColorFromFormattingConditions, isDimensionFormattingRule, isNumericFormattingRule } from '../../FormattingRules/utils';
import { clickthroughPrefix } from '../components/Clickthrough/consts';
import SourceIconsFormatter from '../components/LabelFormatters/SourceIconsFormatter';
import { formatTimeRange } from '../components/TimeFilter/utils';
import {
  DimensionTypes,
  noOverlap,
  OrientationType,
  PeriodBehaviorCapitelized,
  StackedType,
  widgetLimitConfigs,
  widgetTempIdInit,
} from '../constants';
import { filterTimeBucketFromDimensions, hasTimeBucket } from '../EditCustomDashboard/TimeBucketBreakdown/utils';
import {
  AllTypeWidgets,
  BarSubType,
  ColoringRules,
  CustomDashboardDto,
  DonutTypeWidget,
  DwQueryRequest,
  PermissionType,
  PivotTableRequest,
  PivotTableTypeWidget,
  publicId,
  QueryObject,
  ReportsTypes,
  TableSubType,
  TableTypeWidget,
  TableWidget,
  TileSubType,
  TileWidgetVisualization,
  WidgetCategory,
} from '../types';
import {
  AxisTickType,
  ColumnFormatter,
  FieldType,
  LabelFormatterOption,
  labelFormatterOptionToDy,
  labelFormatterOptionToHeight,
} from '../types/types';

export const getRepeatedValueToString = v => (Array.isArray(v) ? v.join(', ') : v);
const removeTempIdFromWidget = ({ id, ...rest }: AllTypeWidgets): AllTypeWidgets =>
  id?.startsWith(widgetTempIdInit) ? rest : { id, ...rest };

export const generateTempIdForWidget = () => `${widgetTempIdInit}${generateID()}`;
export const removeTempIdsFromDashboard = (dashboard: CustomDashboardDto): CustomDashboardDto => ({
  ...dashboard,
  widgets: dashboard.widgets.map(removeTempIdFromWidget),
});

export const isHistoricalQuery = (request: QueryObject) => !!request.timeRange;
export const shouldShowDrillDown = (newWidget: AllTypeWidgets) =>
  [WidgetCategory.Pie, WidgetCategory.Bar].includes(newWidget.category) || newWidget.type === TableSubType.Table;

export const getTimeRangeDescription = (timeRange?: DateCondition): string => {
  if (!timeRange) {
    return '';
  }
  if (timeRange.relative) {
    const { value, unit, periodBehaviour } = timeRange.relative;
    const periodText = PeriodBehaviorCapitelized[periodBehaviour];
    return `${periodText} ${value} ${unit.toLowerCase()}`;
  }
  if (timeRange.between) {
    return `${formatTimeRange(timeRange)}`;
  }
  return '';
};

export const shouldRenderWidget = ({ widget }: { widget: AllTypeWidgets }) => {
  const {
    category,
    type,
    requests: [
      {
        select: { dims, metrics },
      },
    ],
  } = widget;

  if (category === WidgetCategory.Pie) {
    return (dims.length > 0 && metrics.length > 0) || metrics.length > 0;
  }
  if (category === WidgetCategory.Table) {
    if (type === TableSubType.Table) {
      return filterTimeBucketFromDimensions(dims).length > 0 || metrics.length > 0;
    }
    if (type === TableSubType.PivotTable) {
      const colDims = filterTimeBucketFromDimensions(dims.filter(({ type }) => type === DimensionTypes.Col));
      const rowDims = filterTimeBucketFromDimensions(dims.filter(({ type }) => type === DimensionTypes.Row));
      return colDims.length > 0 || rowDims.length > 0 || metrics.length > 0;
    }
  }
  if (category === WidgetCategory.Bar) {
    return dims.length > 0 && metrics.length > 0;
  }
  if (category === WidgetCategory.Line) {
    return dims.length > 0 && metrics.length > 0;
  }
  if (category === WidgetCategory.Tile) {
    if (type === TileSubType.Trend) {
      return metrics.length > 0 && dims.length > 0;
    }
    if (type === TileSubType.Tile) {
      return metrics.length > 0;
    }
  }
  return true;
};

export const shouldEnableWidgetQuery = ({ widget, featureFlags }: { widget: AllTypeWidgets; featureFlags: Record<string, boolean> }) => {
  if (!featureFlags[FeatureFlags.CDOverSelection]) {
    return canRenderWidget({ widget });
  }

  return (
    shouldRenderWidget({ widget }) &&
    (widget.category === WidgetCategory.Text
      ? widget.requests?.[0] && filterTimeBucketFromDimensions(widget.requests[0].select.dims).length > 0
      : true)
  );
};

export const canRenderWidget = ({ widget }: { widget: AllTypeWidgets }) => {
  const {
    category,
    type,
    requests: [
      {
        select: { dims, metrics },
      },
    ],
  } = widget;
  if (category === WidgetCategory.Pie) {
    return (dims.length === 1 && metrics.length === 1) || (dims.length === 0 && metrics.length === 1);
  }
  if (category === WidgetCategory.Table) {
    if (type === TableSubType.Table) {
      return filterTimeBucketFromDimensions(dims).length > 0 || metrics.length > 0;
    }
    if (type === TableSubType.PivotTable) {
      const colDims = filterTimeBucketFromDimensions(dims.filter(({ type }) => type === DimensionTypes.Col));
      const rowDims = filterTimeBucketFromDimensions(dims.filter(({ type }) => type === DimensionTypes.Row));
      return (
        !(colDims.length === 0 && rowDims.length === 0 && metrics.length === 0) &&
        colDims.length < 2 &&
        rowDims.length < 2 &&
        metrics.length < 6
      );
    }
  }
  if (category === WidgetCategory.Bar) {
    return (dims.length === 2 && metrics.length === 1) || (dims.length === 1 && metrics.length < 6 && metrics.length > 0);
  }
  if (category === WidgetCategory.Line) {
    return (
      dims.length <= 2 && dims.length > 0 && filterTimeBucketFromDimensions(dims).length <= 1 && metrics.length < 6 && metrics.length > 0
    ); // supporting historical dim and another dim
  }
  if (category === WidgetCategory.Tile) {
    if (type === TileSubType.Trend) {
      return metrics.length === 1 && dims.length === 1;
    }
    if (type === TileSubType.Tile) {
      return dims.length === 0 && metrics.length === 1;
    }
  }
  return true;
};

export const isValidTimeRange = (timeRange?: DateCondition) => {
  if (!timeRange) {
    return true;
  }
  if (timeRange.relative) {
    const { periodBehaviour, value, unit } = timeRange.relative;
    return !!(periodBehaviour && value && unit);
  } // implement other cases when needed
  return true;
};

export const getPinToAppRows = (rows, selectedApp, allUsers, apps) => {
  const selectedAppName = apps.find(({ name }) => APP_PATHS[name] === selectedApp)?.name;
  return rows
    .filter(row => selectedAppName && !row.apps?.includes(selectedAppName) && !row.predefined)
    .map(row => ({
      ...row,
      createdByUserId: row.createdByUserId ? allUsers[row.createdByUserId] : '',
      sharedUsers:
        row.dashboardReportPermission?.editors.includes(publicId) || row.dashboardReportPermission?.viewers.includes(publicId)
          ? publicId
          : [...(row.dashboardReportPermission?.editors || []), ...(row.dashboardReportPermission?.viewers || [])].map(
              userId => allUsers[userId]
            ),
    }));
};

export const defaultDeliveryConfig = (fileFormat = FileFormat.PDF_FORMAT) => ({
  integrationId: null,
  type: DeliveryMethodOptions.Email,
  recipients: [],
  title: '',
  message: '',
  attachmentMetadata: {
    fileFormat,
  },
});

export const getSubType = (orientation, stacked) => {
  if (orientation === OrientationType.Horizontal) {
    if (stacked === StackedType.None) {
      return BarSubType.BarHorizontal;
    }
    if (stacked === StackedType.Stacked) {
      return BarSubType.StackedHorizontal;
    }
    return BarSubType.Percentage100_Horizontal;
  }
  if (stacked === StackedType.None) {
    return BarSubType.BarVertical;
  }
  if (stacked === StackedType.Stacked) {
    return BarSubType.StackedVertical;
  }
  return BarSubType.Percentage100_Vertical;
};

export const fieldFormatter = (value, type) => {
  if (isNullOrUndefined(value)) {
    return '';
  }
  switch (type) {
    case FormatterType.number:
      return value.toLocaleString();
    case FormatterType.percentage:
      return `${getPercent(value)}`;
    case FormatterType.date:
      return new Date(value).toLocaleString();
    case FormatterType.array:
      return (Array.isArray(value) ? value : [value]).join(', ');
    default:
      return value.toString();
  }
};

const innerLabelStyle = (label, size) => ({
  ...flex.itemsCenter,
  color: 'var(--color-content-base-secondary)',
  position: 'relative',
  right: 6,
  whiteSpace: 'nowrap',
  mr: '-2px',
  fontSize: 12,
  fontWeight: 400,
  ':before': {
    ...flex.center,
    content: `"${label}"`,
    backgroundColor: 'var(--color-surface-base-tertiary)',
    width: 'calc(100% + 16px)',
    position: 'absolute',
    top: size === 'xSmall' ? -3 : size === 'small' ? -6 : -10,
    bottom: size === 'xSmall' ? -3 : size === 'small' ? -6 : -10,
    left: size === 'xSmall' ? -9 : -10,
    borderBottomLeftRadius: 8,
    borderTopLeftRadius: 8,
  },
});

export const inputInnerLabelStartAdornment =
  (label, size = 'xSmall') =>
  () => <Box sx={innerLabelStyle(label, size)}>{label}</Box>;

export const downloadDashboardWidgetsAsJson = (widgets, dashboardName) =>
  downloadBlob(
    JSON.stringify(widgets.map(w => ({ ...w, id: generateUUID() }))),
    'application/json',
    `Avalor_${dashboardName}_dashboard_${generateID()}.json`
  );

export const activeIconStyle = theme => ({
  '&.MuiButtonBase-root.MuiIconButton-root:before': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    boxSizing: 'content-box',
    borderRadius: '10px',
    backgroundColor: 'var(--color-surface-list-hover)',
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shorter,
    }),
  },
});

export const shouldShowTotals = (widget: AllTypeWidgets) => {
  if (widget.type === TableSubType.PivotTable) {
    return widget.definition.custom?.showTotals;
  }

  return (
    (!!(widget as DonutTypeWidget).definition.custom?.showTotals || !!(widget as TableWidget).definition.custom?.showTotals) &&
    !widget.requests[0].select.dims.find(dim => hasTimeBucket(dim.name)) &&
    !!widget.requests[0].select.metrics.length
  );
};

export const isTableWidget = (widget: AllTypeWidgets): widget is TableTypeWidget => widget.type === TableSubType.Table;

export const trendDefaultRequestParams = (request: DwQueryRequest) => ({
  groupBy: [TimeBucket.BUCKET_DAY],
  timeRange: {
    relative: {
      value: 30,
      unit: 'DAYS',
      periodBehaviour: PeriodBehavior.last,
    },
  },
  select: { ...request.select, dims: [{ name: TimeBucket.BUCKET_DAY }] },
});

const shouldApplyFormattingRule = ({
  rule,
  columnFormatterType,
  fieldName,
}: {
  rule: FormattingRule;
  columnFormatterType?: ColumnFormatter;
  fieldName?: string;
}) => {
  if (isDimensionFormattingRule(rule)) {
    return fieldName === rule.fieldName;
  }

  if (isNumericFormattingRule(rule)) {
    return columnFormatterType === ColumnFormatter.HeatMap;
  }

  return false;
};

export const getRowStyle = ({
  selected,
  row,
  cell,
  columnFormatterType,
  hasClickInteraction = true,
  formattingRule,
}: {
  selected: any[];
  row: any;
  cell: HeadCellType;
  columnFormatterType?: ColumnFormatter;
  hasClickInteraction?: boolean;
  formattingRule?: FormattingRule | undefined;
}) => {
  const formattingColor = getColorFromFormattingConditions({ value: row[cell.id], formattingRule });

  const isSelected = isRowSelected({ selected, row, cell });
  const canApplyFormattingRule =
    formattingColor && formattingRule && shouldApplyFormattingRule({ rule: formattingRule, columnFormatterType, fieldName: cell.id });

  return {
    ':hover': {
      background: canApplyFormattingRule ? getCssVarColorWithOpacity(formattingColor, 0.35) : 'var(--color-surface-table-row-active)',
    },
    background: () =>
      canApplyFormattingRule
        ? getCssVarColorWithOpacity(formattingColor, isSelected ? 0.42 : 0.25)
        : isSelected && 'var(--color-surface-table-row-active)',
    ...(hasClickInteraction ? { cursor: 'pointer' } : {}),
  };
};

export const areObjectsEqual = (selectedRow: any, row: any) =>
  Object.keys(selectedRow).every(key => {
    if (key === 'metric') {
      return true;
    }

    const selectedValue = Array.isArray(selectedRow[key]) ? selectedRow[key].join(',') : selectedRow[key];
    const rowValue = row[key] === null ? null : row[key] === 'null' ? null : row[key]?.toString();

    if ((selectedValue === 'null' || selectedValue === null) && rowValue === null) {
      return true;
    }

    return selectedValue?.toString() === rowValue;
  });

export const isRowSelected = ({ selected, row, cell }: { selected: any[]; row: any; cell: HeadCellType }) =>
  selected.some(selectedRow => areObjectsEqual(selectedRow, row) && cell.id === selectedRow.metric);

export const getColumnFormatter = ({
  value,
  type,
  columnFormatter,
  formattingRule,
  fieldName,
}: {
  value: number | string;
  type?: string;
  columnFormatter?: ColumnFormatter;
  formattingRule?: FormattingRule;
  fieldName?: string;
}) => {
  const columnFormatterHandler: Record<ColumnFormatter, () => React.JSX.Element | string | number> = {
    [ColumnFormatter.Progress]: () => {
      const valueIsNumber = typeof value === 'number';
      return !valueIsNumber ? (
        value
      ) : (
        <Box sx={{ ...flex.justifyEndCenter, width: '100%', gap: 1 }}>
          {getPercent(value)}
          <LinearProgress
            value={value}
            variant="determinate"
            sx={{
              borderRadius: 20,
              height: 8,
              width: 48,
              [`& .MuiLinearProgress-bar`]: {
                background: getColorFromFormattingConditions({
                  value,
                  formattingRule,
                  defaultColor: 'var(--color-content-interactive-primary-default)',
                }),
                borderRadius: 0,
              },
              transition: 'ease-in',
            }}
          />
        </Box>
      );
    },
    [ColumnFormatter.SourceIconsWithNames]: () => (
      <SourceIconsFormatter
        value={value.toString()}
        showText
        isVertical={false}
        isXRotated={false}
        sx={{ color: undefined, ...flex.justifyStartCenter }}
        itemMaxWidth="100%"
      />
    ),
    [ColumnFormatter.SourceIcons]: () => (
      <SourceIconsFormatter value={value.toString()} showText={false} isVertical={false} isXRotated={false} />
    ),
    [ColumnFormatter.None]: () => generateInnerDefaultFormatter({ value, type, fieldName }),
    [ColumnFormatter.Text]: () => generateInnerDefaultFormatter({ value, type, fieldName }),
    [ColumnFormatter.HeatMap]: () => generateInnerDefaultFormatter({ value, type, fieldName }),
  };

  return columnFormatter && columnFormatterHandler[columnFormatter] !== undefined
    ? columnFormatterHandler[columnFormatter]()
    : generateInnerDefaultFormatter({ value, type, fieldName });
};

export const getAllGroups = (items: string[]): string[][] =>
  items.reduce<string[][]>((acc, item) => [...acc, ...acc.map(group => [...group, item]), [item]], []);

export const formatDataForVennDiagram = (input: Record<string, number>[] | null | undefined): { key: string[]; data: number }[] => {
  if (!input || input.length === 0) {
    return [];
  }

  const result = Object.entries(input[0])
    .filter(([key]) => key !== 'id')
    .map(([key, data]) => ({
      key: key.split(', ').map(item => item.trim()),
      data,
    }));

  // Sort the result by the length of the 'key' array, and by alphabetical order if the length is the same
  return result.toSorted((a, b) => {
    if (a.key.length === b.key.length) {
      return a.key.join(', ').localeCompare(b.key.join(', '));
    }
    return a.key.length - b.key.length;
  });
};

export const calculateNonOverlapping = (data: { key: string[]; data: number }[], keyColorMap) => {
  const keys = Array.from(new Set(data.flatMap(item => item.key)));

  const result: {
    [key: string]: {
      'No overlap': { value: number; percentage?: string; fill?: string };
      overlaps: { [key: string]: { value: number; percentage: string; fill?: string } };
    };
  } = {};

  keys.forEach(key => {
    const totalKeyValue = data.find(item => item.key.length === 1 && item.key[0] === key)?.data || 0;

    result[key] = { [noOverlap]: { value: totalKeyValue }, overlaps: {} };
    const overlapThree = data.find(item => item.key.length === 3 && item.key.includes(key))?.data || 0;

    keys.forEach(otherSource => {
      if (key !== otherSource) {
        const overlap = data.find(item => item.key.length === 2 && item.key.includes(key) && item.key.includes(otherSource))?.data || 0;
        if (overlap > 0) {
          result[key].overlaps[`Overlaps with ${otherSource}`] = {
            value: overlap,
            percentage: getPercent((overlap / totalKeyValue) * 100),
            fill: keyColorMap[otherSource],
          };
        }
      }
    });

    const overlapTwoData = Object.values(result[key].overlaps).reduce((acc, overlap) => acc + overlap.value, 0);
    const noOverlapValue = totalKeyValue - overlapTwoData + overlapThree;
    result[key][noOverlap] = {
      value: noOverlapValue,
      percentage: getPercent((noOverlapValue / totalKeyValue) * 100),
      fill: keyColorMap[key],
    };
  });

  return result;
};

export const getArrowIndication = ({
  coloringRule,
  comparisonDifference,
  showNoChangeImage = false,
  textSx,
  hideNoChange = false,
}: {
  coloringRule: ColoringRules | undefined;
  comparisonDifference: { isPercentage: boolean; value: number; isValid: boolean };
  showNoChangeImage?: boolean;
  textSx?: SxProps;
  hideNoChange?: boolean;
}) => {
  const { isPercentage, value, isValid } = comparisonDifference;
  if (!isValid) {
    return undefined;
  }
  const commonSx = { ...flex.justifyStartCenter, fontSize: 13, fontWeight: 600, ml: -0.5 };

  const displayNumber = `${isPercentage ? '' : value >= 0 ? '+' : '-'}${abbreviateNumber(Math.abs(value))}${isPercentage ? '%' : ''}`;
  const isNoChange = parseInt(value.toFixed(2)) === 0;

  const upIsGood = coloringRule === ColoringRules.UP_IS_GOOD;

  if (isNoChange && hideNoChange) return undefined;

  return isNoChange ? (
    <Box
      sx={{
        height: '100%',
        ...flex.justifyStartCenter,
        ...textSx,
      }}>
      {showNoChangeImage && (
        <Box sx={{ mt: 1, mr: 1 }}>
          <NoChange />
        </Box>
      )}
      No change
    </Box>
  ) : value > 0 ? (
    <Box
      sx={{
        ...commonSx,
        color: upIsGood ? 'var(--color-content-status-success-secondary)' : 'var(--color-content-status-danger-secondary)',
      }}>
      <Up />
      {displayNumber}
    </Box>
  ) : (
    <Box
      sx={{
        ...commonSx,
        color: upIsGood ? 'var(--color-content-status-danger-secondary)' : 'var(--color-content-status-success-secondary)',
      }}>
      <Down />
      {displayNumber}
    </Box>
  );
};

export const getComparisonDifference = (data, metric): { isPercentage: boolean; value: number; isValid: boolean } => {
  if (!data || data.length < 2 || !metric || data[0][metric] === undefined) {
    return { isPercentage: false, value: 0, isValid: false };
  }

  const firstValue = data[0][metric];
  const lastValue = data[data.length - 1][metric];

  if (firstValue === 0) {
    return { isPercentage: false, value: lastValue, isValid: true };
  }

  return { value: ((lastValue - firstValue) / Math.abs(firstValue)) * 100, isValid: true, isPercentage: true };
};

export const ComparisonIndicator = ({
  data,
  metric,
  coloringRule,
  showNoChangeImage,
  textSx,
}: {
  data: Record<string, any>;
  metric: string;
  coloringRule: ColoringRules;
  showNoChangeImage?: boolean;
  textSx?: SxProps;
}) => {
  const comparisonDifference = getComparisonDifference(data, metric);
  return getArrowIndication({ comparisonDifference, coloringRule, showNoChangeImage, textSx });
};

export const getDyPerWidget = ({
  labelFormatter,
  rotation,
  widgetType,
}: {
  labelFormatter?: LabelFormatterOption;
  rotation: AxisTickType;
  widgetType: WidgetCategory;
}) => labelFormatter && labelFormatterOptionToDy[labelFormatter] && labelFormatterOptionToDy[labelFormatter]?.[widgetType]?.[rotation];

export const getHeightPerWidget = ({
  labelFormatter,
  rotation,
  widgetType,
}: {
  labelFormatter?: LabelFormatterOption;
  rotation: AxisTickType;
  widgetType: WidgetCategory;
}) =>
  (labelFormatter &&
    labelFormatterOptionToHeight[labelFormatter] &&
    labelFormatterOptionToHeight[labelFormatter]?.[widgetType]?.[rotation]) ||
  DEFAULT_COMPOSED_CHART_X_HEIGHT;

export const truncateText = ({ text, maxWidth, fontFamily = 'Inter' }: { text: string; maxWidth: number; fontFamily?: string }): string =>
  (calculateTextWidthCavnas({ text, fontSize: '13', fontFamily }) || 0) <= maxWidth
    ? text
    : text
        .split('')
        .reduce(
          (acc, _, i, arr) =>
            (calculateTextWidthCavnas({ text: `${arr.slice(0, i).join('')}...`, fontSize: '13', fontFamily }) || 0) > maxWidth
              ? acc
              : `${arr.slice(0, i).join('')}...`,
          ''
        );

export const getInitialWidgetData = (widgetId: string, initialWidgets) => initialWidgets.find(({ id }) => id === widgetId);

export const defaultDashboard: CustomDashboardDto = {
  type: ReportsTypes.Visual,
  active: false,
  name: 'New Dashboard',
  intervalExpression: '0 0 * * *',
  deliveryConfig: defaultDeliveryConfig(),
  description: '',
  apps: [],
  widgets: [],
  customQueries: null,
  dashboardReportPermission: {
    viewers: [],
    editors: [],
    type: PermissionType.private,
  },
  tags: [],
};

export const getCleanRequestsFromTileWidget = (newWidget: AllTypeWidgets) => [
  {
    ...newWidget.requests[0],
    select: {
      ...newWidget.requests[0].select,
      metrics: newWidget.requests[0].select.metrics.map(({ name, alias }) => ({ name, alias })),
    },
  },
];

const getCleanRequestFromPivotTableWidget = (newWidget: PivotTableTypeWidget) => {
  const { sortingCol, ...rest } = newWidget.requests[0];

  return [{ ...rest, select: { ...rest.select, dims: [] }, sorting: [], groupBy: [] }];
};

export const cleanRequests = (newWidget: AllTypeWidgets) => {
  if (newWidget.type === TileSubType.Tile && newWidget.definition.custom.visualization === TileWidgetVisualization.Progress) {
    return getCleanRequestsFromTileWidget(newWidget);
  }

  if (newWidget.type === TableSubType.PivotTable) {
    return getCleanRequestFromPivotTableWidget(newWidget);
  }

  return newWidget.requests;
};

export const checkIsGroupOthersDisabled = (widget: AllTypeWidgets) => {
  const { dims } = widget.requests[0].select;
  const initialCondition = dims.length !== 1;

  if (widget.type === TableSubType.PivotTable) {
    return initialCondition || dims.filter(({ type }) => type === DimensionTypes.Col).length !== 0;
  }

  if (widget.type === TableSubType.Table) {
    return initialCondition || !widget.definition?.custom?.topN;
  }

  return initialCondition || !widget.requests[0].top?.size;
};

export const getGlobalFiltersFromDashboardFilters = (
  filters: Record<string, any>,
  defaultFiltersObject: Record<string, any>
): Record<string, any> =>
  Object.keys(filters).reduce(
    (acc, cur) =>
      Object.prototype.hasOwnProperty.call(defaultFiltersObject, cur) || cur.startsWith(clickthroughPrefix)
        ? acc
        : {
            ...acc,
            [cur]: (Array.isArray(filters[cur]) ? filters[cur] : [filters[cur]])?.map(v => (v === 'null' ? null : v)),
          },
    {}
  );

export const getWidgetLimit = (
  widget: { category: WidgetCategory; type: string; requests: DwQueryRequest[] | PivotTableRequest[] },
  fieldCategory: FieldType
): number | undefined => {
  const { category, type } = widget;
  const { dims, metrics } = widget.requests[0].select;

  if (type === TableSubType.PivotTable && fieldCategory === FieldType.Dimensions) {
    return 1;
  }

  const config = widgetLimitConfigs[type] || widgetLimitConfigs[category];

  if (!config) {
    return undefined;
  }

  if (fieldCategory === FieldType.Measurements) {
    return config.metrics({ selectedDims: dims });
  }
  if (fieldCategory === FieldType.Dimensions) {
    return config.dims({ selectedMetrics: metrics });
  }
  return undefined;
};

export const getSelectedPieChartData = (selected, parsedData, dimNameOrAlias) => {
  const selectedWithIndex = selected.map(v => ({
    ...v,
    index: parsedData?.findIndex(
      d =>
        (d.originalValue === 'null' || d.originalValue === null
          ? null
          : typeof d[dimNameOrAlias] === 'object' && !Array.isArray(d[dimNameOrAlias])
            ? JSON.stringify(d[dimNameOrAlias])
            : typeof d[dimNameOrAlias] === 'boolean'
              ? d[dimNameOrAlias]
              : Array.isArray(d[dimNameOrAlias])
                ? getRepeatedValueToString(d[dimNameOrAlias])
                : d[dimNameOrAlias]?.toString()) === getRepeatedValueToString(v.value)
    ),
  }));

  return selectedWithIndex.filter(v => v.index !== -1);
};
